import { useState } from "react";
import { MoreVertical, Edit, Phone, CheckCircle, XCircle, Clock, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { CallNotesDialog } from "./CallNotesDialog";
import { CallDetailsDialog } from "./CallDetailsDialog";

interface CallActionsMenuProps {
  callId: string;
  callData: any;
  onStatusUpdate: (callId: string, status: string) => void;
  onPriorityUpdate: (callId: string, priority: string) => void;
  onRefresh: () => void;
}

export function CallActionsMenu({ 
  callId, 
  callData, 
  onStatusUpdate, 
  onPriorityUpdate,
  onRefresh 
}: CallActionsMenuProps) {
  const [notesOpen, setNotesOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm">
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>Actions</DropdownMenuLabel>
          <DropdownMenuSeparator />
          
          <DropdownMenuItem onClick={() => setDetailsOpen(true)}>
            <Edit className="mr-2 h-4 w-4" />
            View Details
          </DropdownMenuItem>
          
          <DropdownMenuItem onClick={() => setNotesOpen(true)}>
            <Edit className="mr-2 h-4 w-4" />
            Add Note
          </DropdownMenuItem>
          
          <DropdownMenuSeparator />
          <DropdownMenuLabel>Update Status</DropdownMenuLabel>
          
          <DropdownMenuItem onClick={() => onStatusUpdate(callId, 'contacted')}>
            <CheckCircle className="mr-2 h-4 w-4" />
            Mark Contacted
          </DropdownMenuItem>
          
          <DropdownMenuItem onClick={() => onStatusUpdate(callId, 'qualified')}>
            <CheckCircle className="mr-2 h-4 w-4 text-green-500" />
            Mark Qualified
          </DropdownMenuItem>
          
          <DropdownMenuItem onClick={() => onStatusUpdate(callId, 'callback_scheduled')}>
            <Clock className="mr-2 h-4 w-4" />
            Schedule Callback
          </DropdownMenuItem>
          
          <DropdownMenuItem onClick={() => onStatusUpdate(callId, 'no_answer')}>
            <Phone className="mr-2 h-4 w-4" />
            No Answer
          </DropdownMenuItem>
          
          <DropdownMenuItem onClick={() => onStatusUpdate(callId, 'unqualified')}>
            <XCircle className="mr-2 h-4 w-4" />
            Mark Unqualified
          </DropdownMenuItem>
          
          <DropdownMenuSeparator />
          <DropdownMenuLabel>Priority</DropdownMenuLabel>
          
          <DropdownMenuItem onClick={() => onPriorityUpdate(callId, 'urgent')}>
            <AlertCircle className="mr-2 h-4 w-4 text-red-500" />
            Urgent
          </DropdownMenuItem>
          
          <DropdownMenuItem onClick={() => onPriorityUpdate(callId, 'high')}>
            <AlertCircle className="mr-2 h-4 w-4 text-orange-500" />
            High
          </DropdownMenuItem>
          
          <DropdownMenuItem onClick={() => onPriorityUpdate(callId, 'medium')}>
            <AlertCircle className="mr-2 h-4 w-4 text-yellow-500" />
            Medium
          </DropdownMenuItem>
          
          <DropdownMenuItem onClick={() => onPriorityUpdate(callId, 'low')}>
            <AlertCircle className="mr-2 h-4 w-4 text-gray-500" />
            Low
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <CallNotesDialog 
        open={notesOpen}
        onOpenChange={setNotesOpen}
        callId={callId}
        onSuccess={onRefresh}
      />

      <CallDetailsDialog
        open={detailsOpen}
        onOpenChange={setDetailsOpen}
        callData={callData}
        onUpdate={onRefresh}
      />
    </>
  );
}